import {DatePipe} from '@angular/common';
import {Injectable} from '@angular/core';
import {CloseInvertEnum} from '@model/enum/close-invert.enum';
import {LiquidationTypeEnum} from '@model/enum/liquidation-type.enum';
import {MarketTypeEnum} from '@model/enum/market-type.enum';
import {MarketEnum} from '@model/enum/market.enum';
import {SideEnum} from '@model/enum/side.enum';
import {TypeOrderEnum} from '@model/enum/type-order.enum';
import {HistoryFilterOrder} from '@model/webFeeder/history-filter-order';
import {HistoryOrder} from '@model/webFeeder/history-order.model';
import {MessageTrade} from '@model/webFeeder/message-trade-order';
import {MessageCancelTrade} from '@model/webFeeder/message-trade-order-cancel';
import {HttpService} from '@services/http-service';
import {INegotiationLoggable} from '@services/strategy-pattern-login/iNegotiation-loggable';
import {ConfigService} from '@services/webFeeder/config.service';
import {Constants} from '@utils/constants';
import {BehaviorSubject, map, Observable, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class INegotiationApiService implements INegotiationLoggable {

  private _quantityOpenOrder = new BehaviorSubject<number>(0);
  quantityOpenOrder$ = this._quantityOpenOrder.asObservable();
  public _strategiesList = [];
  public _brokerAccountList = [];
  public marketTypeEnum = MarketTypeEnum;

  constructor(
    private _httpService: HttpService,
    private _httpClient: HttpClient,
    private _configService: ConfigService,
    private _datePipe: DatePipe
  ) { }

  setQuantityOpenOrder(qtd: number) {
    this._quantityOpenOrder.next(qtd);
  }

  brokerServiceAccountList(account: string): any {
    if (!account) {
      return of([]);
    } else {
      if (this._brokerAccountList.length === 0 || !this._brokerAccountList) {
        return this._httpClient
          .get(this._configService.getApiWebFeederRest() + '/services/negotiation/brokerAccountList?login=' + account)
          .pipe(map((response: any) => {
            if (response && response.accountsBean) {
              return response.accountsBean;
            } else if (response && response.length > 0) {
              return response;
            }
            return [];
          }));
      } else {
        return of(this._brokerAccountList);
      }
    }
  }

  financialAccountInformationCompl(account: string, market: string) {
    if (!account || !market) {
      return of([]);
    } else {
      return this._httpClient
        .get(this._configService.getApiWebFeederRest() + '/services/negotiation/financialAccountInformationCompl?account='
          + account + '&market=' + market)
        .pipe(map((response: any) => {
          if (response) {
            return response.financialInformationBean;
          }
          return [];
        }));
    }
  }

  positionBovespa(account: string, quote?: string) {
    let url;
    if (quote) {
      url = 'services/negotiation/positionBovespa?account=' + account + '&quote=' + quote;
    } else {
      url = 'services/negotiation/positionBovespa?account=' + account;
    }
    return this._httpClient
      .get(this._configService.getApiWebFeederRest() + '/' + url)
      .pipe(map((response: any) => {
        if (response) {
          return response;
        }
        return [];
      }));
  }

  positionBmf(account: string, quote?: string) {
    let url;
    if (quote) {
      url = 'services/negotiation/positionBmf?account=' + account + '&quote=' + quote;
    } else {
      url = 'services/negotiation/positionBmf?account=' + account;
    }
    return this._httpService
      .doGet(this._configService.getApiWebFeederRest(), url, false)
      .pipe(map(response => {
        if (response) {
          const json = response;
          return json;
        }
        return [];
      }));
  }

  getHistoryOrder(account: string, market: string, dataInicio: string, dataFim: string,
    symbol: string, status: string, direction: string, orderID?: string, type?: string, statusGeneric?: string, statusGroup?: string
  ): Observable<any> {
    const params = new HistoryFilterOrder();

    params.account = account;
    params.dataInicio = dataInicio || null;
    params.dataFim = dataFim || null;
    params.orderID = orderID || null;
    params.market = '-';
    if (market == '1') {
      params.market = Constants.MARKET_BOVESPA;
    } else if (market == '2') {
      params.market = Constants.MARKET_BMF;
    }
    if (symbol && symbol !== null) {
      params.symbol = symbol.toUpperCase();
    } else {
      params.symbol = null;
    }
    if (direction && direction !== null) {
      params.direction = direction.toUpperCase();
    } else {
      params.direction = null;
    }
    if (type == '') {
      type = null;
    }
    params.type = type;
    params.statusGroup = statusGroup;

    if (status == 'New') {
      params.statusGroup = 'Abertas';
    } else {
      if (status && status !== null) {
        params.status = status.toUpperCase();
      } else {
        params.status = null;
      }
    }

    if (params.dataInicio !== null || params.dataFim !== null || params.orderID !== null) {
      params.queryType = '1';
    } else {
      params.queryType = '0';
    }
    return this.getApi(params);
  }

  private getApi(params: HistoryFilterOrder) {
    const headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    return this._httpClient
      .post(this._configService.getApiWebFeederRest() + '/services/negotiation/getHistoryOrder/', JSON.stringify(params), {headers})
      .pipe(map((response: any) => {
        if (response) {
          if (response.error) {
            return [];
          }
          try {
            return response;
          } catch (e) {
          }
        }
        return [];
      }));
  }

  // WSO2
  sendOrderWso2(account: string, quote: string, fractionaryQtd: number, nonFractionaryQtd: number, price: string, market: MarketEnum,
    type: TypeOrderEnum, side: SideEnum, validityOrderTemp: Date, orderStrategy: string, timeinforce?: string, orderid?: string,
    origclordid?: string, loteDefault?: number, maxfloor?: string, minqty?: string, ordertag?: string, targettrigger?: string,
    targetlimit?: string, stoptrigger?: string, stoplimit?: string, isStop?: boolean
  ): Observable<any> {
    throw new Error('Method not implemented.');
  }

  cancelOrderWso2(account: string, username: string, orderid: string, origclordid: string, market: string,
    quote: string, qtd: string, side: string, enteringtrader: string) {
    throw new Error('Method not implemented.');
  }

  cancelAllOrderHistoryWso2(historyOrder: HistoryOrder[]) {
    throw new Error('Method not implemented.');
  }

  // API
  sendOrderApi(account: string, quote: string, qtd: number, price: string, market: any, type: any, side: SideEnum,
    validityOrder: Date, orderStrategy: string, timeinforce?: string, orderid?: string, origclordid?: string,
    loteDefault?: number, maxfloor?: string, minqty?: string, ordertag?: string, bypasssuitability?: boolean,
    clordlinkid?: string): Observable<any> {
    const params = new MessageTrade();
    params.origclordid = null;
    params.movingstart = null;
    params.initialchangetype = null;
    params.initialchange = null;
    params.pricepercentage = null;
    params.enteringtrader = null;

    params.orderid = orderid || null;
    params.origclordid = origclordid || null;
    params.targettrigger = null;
    params.targetlimit = null;
    params.stoptrigger = null;
    params.stoplimit = null;
    params.validityorder = timeinforce === 'GTD' ? validityOrder : null;
    params.username = this._configService.getBrokerAccount();
    params.account = account;
    params.price = price;
    params.quote = quote;
    params.qtd = qtd;
    params.market = Number(market) && market === MarketEnum.BOVESPA ? Constants.MARKET_BOVESPA :
      Number(market) && market === MarketEnum.BMF ? Constants.MARKET_BMF : market;
    type = type ? type.toString().toLowerCase() : type;
    params.loteDefault = loteDefault ? loteDefault : params.market === Constants.MARKET_BMF ? 1 : 100;
    switch (type) {
      case 'Mercado com proteção':
        params.type = 'marketProtected';
        break;
      case 'limitada':
        params.type = 'limited';
        break;
      case 'stop':
        params.type = 'stop';
        break;
      case 'mercado':
        params.type = 'market';
        break;
      default:
        params.type = type;
        break;
    }
    params.side = side === SideEnum.BUY ? Constants.ORDER_SIDE_BUY : Constants.ORDER_SIDE_SELL;
    params.orderStrategy = orderStrategy;
    params.timeinforce = timeinforce;

    params.maxfloor = maxfloor;
    params.minqty = minqty;
    params.ordertag = ordertag;

    params.bypasssuitability = bypasssuitability;
    params.appName = Constants.DEFAULT_APP_NAME;
    params.clordlinkid = clordlinkid ? clordlinkid : null;
    params.contingencytype = clordlinkid ? '1' : null;
    return this.sendApi(params);
  }

  sendOrderStopApi(account: string, validityOrder: Date, orderStrategy: string, quote: string, qtd: number, targettrigger: string,
    targetlimit: string, stoptrigger: string, stoplimit: string, market: MarketEnum, side: SideEnum, timeinforce?: string,
    orderid?: string, origclordid?: string, loteDefault?: number, isStart?: boolean, maxfloor?: string, minqty?: string,
    orderTag?: string, bypasssuitability?: boolean, clordlinkid?: string): Observable<any> {
    const params = new MessageTrade();
    params.username = this._configService.getBrokerAccount();
    params.account = account;
    params.quote = quote;
    params.qtd = qtd;
    params.orderid = orderid || null;
    params.origclordid = origclordid || null;
    params.validityorder = timeinforce === 'GTD' ? validityOrder : null;
    params.targettrigger = targettrigger;
    params.targetlimit = targetlimit;
    params.stoptrigger = stoptrigger;
    params.stoplimit = stoplimit;
    params.timeinforce = timeinforce;
    params.orderStrategy = orderStrategy;
    params.maxfloor = maxfloor;
    params.minqty = minqty;
    params.ordertag = orderTag;
    params.loteDefault = loteDefault ? loteDefault : params.market === Constants.MARKET_BMF ? 1 : 100;
    params.market = Number(market) === MarketEnum.BOVESPA ? Constants.MARKET_BOVESPA : Constants.MARKET_BMF;
    params.type = isStart ? Constants.ORDER_TYPE_START : Constants.ORDER_TYPE_STOP;
    params.side = side === SideEnum.BUY ? Constants.ORDER_SIDE_BUY : Constants.ORDER_SIDE_SELL;
    params.bypasssuitability = bypasssuitability;
    params.appName = Constants.DEFAULT_APP_NAME;
    params.clordlinkid = clordlinkid ? clordlinkid : null;
    params.contingencytype = clordlinkid ? '1' : null;
    return this.sendApi(params);
  }

  private sendApi(params: MessageTrade) {
    // const searchParams = Object.keys(params)
    //   .pipe(map(key => {
    //     return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
    //   }).join('&');
    if (params.orderid) {
      this._configService.writeLog(params, 'SendEditOrder');
      return this._httpClient
        .post(this._configService.getApiWebFeederRest() + '/services/negotiation/SendEditOrder', params)
        .pipe(map(response => {
          if (response) {
            return response;
          }
          return [];
        }));
    } else {
      this._configService.writeLog(params, 'sendNewOrderFractional');
      return this._httpClient
        .post(this._configService.getApiWebFeederRest() + '/services/negotiation/sendNewOrderFractional', params)
        .pipe(map(response => {
          if (response) {
            return response;
          }
          return [];
        }));
    }
  }

  cancelOrderApi(account: string, username: string, orderid: string, origclordid: string, market: string,
    quote: string, qtd: string, side: string, enteringtrader: string) {
    const params = new MessageCancelTrade();
    params.username = this._configService.getBrokerAccount();
    params.orderid = orderid;
    params.origclordid = origclordid;
    params.market = market;
    params.quote = quote;
    params.qtd = qtd;
    params.side = side;
    params.enteringtrader = enteringtrader || null;
    params.account = account;
    params.appName = Constants.DEFAULT_APP_NAME;
    return this._httpClient.post(this._configService.getApiWebFeederRest() + '/services/negotiation/cancelOrder', params)
      .pipe(map(response => {
        return response ? response : [];
      }));
  }

  cancelAllOrderHistoryApi(historyOrder: HistoryOrder[]) {
    return this._httpService
      .doPost(this._configService.getApiWebFeederRest(), 'services/negotiation/CancelAllOrderHistory', historyOrder, false)
      .pipe(map(response => {
        if (response) {
          const json = response;
          return json;
        }
        return [];
      }));
  }

  brokeragePDF(account: string, market: string, date: Date) {
    if (!account || !market || !date) {
      return of([]);
    } else {
      date = new Date(date);
      let day = date.getDate().toString();
      let month = (date.getMonth() + 1).toString();
      const year = date.getFullYear().toString();
      if (Number(month) < 10) {
        month = 0 + month;
      }
      if (Number(day) < 10) {
        day = 0 + day;
      }
      const datetemp = day + month + year;
      const params = '?account=' + account + '&market=' + market + '&date=' + datetemp;
      return this._httpService
        .doGetPdf(this._configService.getApiWebFeederRest(), 'services/negotiation/brokeragePDF' + params)
        .pipe(map(((data) => data)));
    }
  }

  brokerageDates(account: string, market: string, startDate: string, endDate: string) {
    if (!account || !market) {
      return of([]);
    } else {
      let url = '';
      const params = '?account=' + account + '&startDate=' + startDate + '&endDate=' + endDate;
      if (this.marketTypeEnum.BOVESPA === market.toUpperCase()) {
        url = 'services/negotiation/brokerageDatesRequestBovespa';
      } else {
        url = 'services/negotiation/brokerageDatesRequestBmf';
      }
      return this._httpService
        .doGet(this._configService.getApiWebFeederRest(), url + params)
        .pipe(map(((data) => data)));
    }
  }

  statementAccount(account: string, market: string, dateStart: Date, dateEnd: Date, ipAddress: string) {
    if (!account || !market || !dateStart || !dateEnd) {
      return of([]);
    } else {
      let dayStart = dateStart.getDate().toString();
      let dayEnd = dateEnd.getDate().toString();
      let monthStart = (dateStart.getMonth() + 1).toString();
      let monthEnd = (dateEnd.getMonth() + 1).toString();
      const yearStart = dateStart.getFullYear().toString();
      const yearEnd = dateEnd.getFullYear().toString();
      if (Number(dayStart) < 10) {
        dayStart = 0 + dayStart;
      }
      if (Number(dayEnd) < 10) {
        dayEnd = 0 + dayEnd;
      }
      if (Number(monthStart) < 10) {
        monthStart = 0 + monthStart;
      }
      if (Number(monthEnd) < 10) {
        monthEnd = 0 + monthEnd;
      }
      const datetempStart = dayStart + monthStart + yearStart;
      const datetempEnd = dayEnd + monthEnd + yearEnd;
      const params = '?account=' + account + '&market=' + market + '&dateStart=' + datetempStart + '&dateEnd=' + datetempEnd;
      return this._httpService
        .doGet(this._configService.getApiWebFeederRest(), 'services/negotiation/statementAccount' + params)
        .pipe(map((res: any) => {
          if (res) {
            const json = res;
            return json;
          }
          return [];
        }));
    }
  }

  getAfcSearchQuestions() {
    return this._httpService
      .doGet(this._configService.getApiWebFeederRest(), 'services/Negotiation/GetAfcSearchQuestions')
      .pipe(map(response => {
        if (response) {
          const json = response;
          return json;
        }
        return [];
      }));
  }

  postAfcSearchQuestions(questionResp: string, questionType: string) {
    const params = {
      questionResp: questionResp,
      questionType: questionType
    };
    return this._httpService
      .doPost(this._configService.getApiWebFeederRest(), 'services/Negotiation/PostAfcSearchQuestions', params, false)
      .pipe(map(response => {
        if (response) {
          const json = response;
          return json;
        }
        return [];
      }));
  }

  getStrategies() {
    const configWhitelabel = this._configService.getConfigWhitelabel();
    if (configWhitelabel && configWhitelabel.billet.getStrategiesService) {
      if (this._strategiesList.length === 0 || !this._strategiesList) {
        return this._httpService
          .doGet(this._configService.getApiWebFeederRest(), 'services/Negotiation/GetStrategies')
          .pipe(map(response => {
            if (response && response.strategies) {
              this._strategiesList = response.strategies;
              return this._strategiesList;
            }
            return [];
          }));
      } else {
        return of(this._strategiesList);
      }
    } else {
      return of([]);
    }
  }

  portfolioHistory(account: string, eventDate: string) {
    const initialDateTemp = eventDate ? this._datePipe.transform(eventDate, 'ddMMyyyy') : null;
    return this._httpService
      .doPost(this._configService.getApiWebFeederRest(),
        'services/Negotiation/portfolioHistory?account=' + account + '&portfolioType=' + '5' + '&eventDate=' + initialDateTemp,
        null, false)
      .pipe(map(response => {
        if (response && response.listBeans) {
          return response.listBeans;
        }
        return [];
      }));
  }

  getEarnings(account: string, initialDate: Date, endDate: Date, filterType: number) {
    const accountTemp: any = this._configService.getBrokerAccountList();
    const cpfCnpj = accountTemp.find(x => x.account === account).cpfCnpj;
    const initialDateTemp = initialDate ? this._datePipe.transform(initialDate, 'ddMMyyyy') : null;
    const endDateTemp = endDate ? this._datePipe.transform(initialDate, 'ddMMyyyy') : null;
    return this._httpService
      .doGet(this._configService.getApiWebFeederRest(),
        'services/Negotiation/GetEarnings?account=' + account + '&cpfCnpj=' + cpfCnpj + '&initialDate='
        + initialDateTemp + '&endDate=' + endDateTemp + '&filterType=' + filterType)
      .pipe(map(response => {
        if (response && response.listBeans) {
          return response.listBeans;
        }
        return [];
      }));
  }

  geBalancetExternalInfo(getLimite: boolean) {
    throw new Error('Method not implemented.');
  }

  customLiquidationClient(symbol: string, market: MarketEnum) {
    if (!symbol || !market) {
      return of([]);
    }
    const params = {
      account: this._configService.getBrokerAccountSelected(),
      symbol: symbol,
      market_id: market === MarketEnum.BOVESPA ? Constants.MARKET_BOVESPA : Constants.MARKET_BMF,
      custom_liquidation_request_type: LiquidationTypeEnum.DAILY, //  total = 1 | daily = 2
      close_or_invert_position: CloseInvertEnum.CLOSE, // close = 1 | invert = 2
      block_opened_orders: true, // fecha as ordens em aberto
      application_name: Constants.DEFAULT_APP_NAME
      // 'order_strategy': 'DAYTRADE', // opcional (Default do OMS)
    };
    return this._httpService
      .doPost(this._configService.getApiWebFeederRest(), 'services/negotiation/position_reset', params, false)
      .pipe(map(response => {
        return response ? response : [];
      }));

  }

}
